import { QuoteType } from "../../types/QuoteType";
import { YesNo } from "../enums";

export interface RentalQuote {
  quoteName: string;
  quoteId?: number | null;
  brokerFee: number;
  borrowerIsReferral: YesNo;
  appraisedVal1: number;
  creditScore: number;
  submissionType: string;
  annualTaxes: number;
  propertiesNY?: YesNo;
  annualInsurance: number;
  refiL1cFixNFlip: YesNo;
  buyUpDownOption: string;
  username: string;
  numOfProperties: number;
  overrideMaster: boolean;
  exceptionPricingOverride?: RentalOverrideOptions;
  annualFloodInsurance: number;
  borrowingEntity: string;
  scenario?: Scenario[];
  mortgagePayoffs: number;
  quoteType: QuoteType;
  appraisedVal3: number;
  totalCosts1: number;
  renovationCostsAfterPurchase?: number;
  monthlyRent: number;
  sreoPropertyNumber: number;
  productType: string;
  purchasePrice: number;
  prepaymentPenalty: string;
  borrowerNetWorth: number;
  yieldSpread: number;
  originationFeeAdjustment?: number;
  annualHOA: number;
  base: Base;
  buyUpDownPercentage: number;
  brokerProcessingFee: number;
  appraisalFee: number;
  usCitizen: YesNo;
  isTier5Borrower: YesNo;
  sfaccountid?: string;
  sfdealid?: string;
  campaignId?: string;
  campaignName?: string;
  campaignExpirationDate?: string;
  campaignIncentive?: string;
  campaignCode?: string;
  hasSubmittedQuote?: boolean;
  isZeroOrigFee?: YesNo;
  underwriterStatus?: string;
  underwriterFields?: RentalUnderwriterFields;
  seasonalRental?: YesNo;
  fiveNineUnit?: YesNo;
  state?: string;
  propertyAddress?: string;
  city?: string;
  zip?: string;
  formattedAddress?: string;
  lenderFinance?: RentalLolaFields;
  submittedDate?: string;
  loanCostOverride?: LoanCostOverride;
  vacantProperty?: YesNo;
  twoFourUnit?: YesNo;
  mortgagePayoffs2: number;
  originalPurchasePrice?: number;
  originalPurchaseDate?: string;
  targetCloseDate?: string;
  wholesalePartner?: string;
  documentPrepFee?: string;
}

export interface RentalPremierQuote extends RentalQuote {
  premier?: RentalPremierOptions;
}

export class RentalPremierOptions {
  propertyManagementFee: number = 0;
  collateralCondos: YesNo = YesNo.No;
  collateral: YesNo = YesNo.No;
  index: string = "5yr I/R Swap";
  openPrepayOption: string = "Open Prepayment Allowance: 0% UPB/Year";
}

export class Scenario {
  scenarioId: number = 1;
  ltvPercentage: number = 75;
  included: YesNo = YesNo.Yes;
  preferred: YesNo = YesNo.No;
}

export class Base {
  condo: YesNo = YesNo.No;
  fixToRent: YesNo = YesNo.No;
  propertyVal: YesNo = YesNo.No;
}

export class RentalOverrideOptions {
  finalOrigFeeOverride?: number | string = "";
  rateReductionOverride?: number | string = "0";
  finalRateOverride?: number | string = "";
  exceptionType?: string = "";
  autoRate?: number | string = "0";
  autoFee?: number | string = "0";
  commissionReduction?: number | string = "0";
  processingFeeOverride?: number | string = "";
  splitProcessingFee?: string = "";
  achRequired?: string = YesNo.Yes;
  achExempt?: string = "Non-exempt";

  constructor(entity: any) {
    if (!entity) return;

    [
      "rateReductionOverride",
      "finalOrigFeeOverride",
      "commissionReduction",
      "finalRateOverride",
      "exceptionType",
      "autoRate",
      "autoFee",
      "processingFeeOverride",
      "splitProcessingFee",
      "achRequired",
      "achExempt",
    ].forEach((prop: string) => {
      const key = prop as keyof RentalOverrideOptions;
      this[key] = entity[prop];
    });
  }
}

export class RentalUnderwriterFields {
  legalFee?: number | string = "";
  taxImpound?: number | string = "";
  insuranceImpound?: number | string = "";

  constructor(entity: any) {
    if (!entity) return;

    ["legalFee", "taxImpound", "insuranceImpound"].forEach((prop: string) => {
      const key = prop as keyof RentalUnderwriterFields;
      this[key] = entity[prop];
    });
  }
}

export class RentalLolaFields {
  interestRateAddOn?: number | string = 0;
  originationPointsAddOn?: number | string = 0;
  applicationFee?: number | string;
  documentFee?: number | string;
  processingFee?: number | string = 0;
  lenderFinanceAppraisalFee?: number | string;
  rpolt6?: boolean;
  rpogt6?: boolean;
  purchase?: boolean;

  constructor(entity: any) {
    if (!entity) return;

    [
      "interestRateAddOn",
      "originationPointsAddOn",
      "applicationFee",
      "documentFee",
      "processingFee",
      "lenderFinanceAppraisalFee",
      "rpolt6",
      "rpogt6",
      "purchase",
    ].forEach((prop: string) => {
      const key = prop as keyof RentalLolaFields;
      this[key] = entity[prop];
    });
  }
}

export class LoanCostOverride {
  iREscrowOverrideInt?: number;
  iREscrowOverrideMoneyAmt?: number;
  taxEscrowAmount?: number;
  insEscrowAmount?: number;

  constructor(entity: any) {
    if (!entity) return;

    [
      "iREscrowOverrideInt",
      "iREscrowOverrideMoneyAmt",
      "taxEscrowAmount",
      "insEscrowAmount",
    ].forEach((prop: string) => {
      const key = prop as keyof LoanCostOverride;
      this[key] = entity[prop];
    });
  }
}
